import type React from "react";

import { AdCopyEvalDemo } from "@/components/adcopy-eval-demo";
import { ImageEvalDemo } from "@/components/image-eval-demo";
import { LandingEvalDemo } from "@/components/landing-eval-demo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AnimatePresence, motion } from "framer-motion";
import { ExternalLink, Maximize2, X } from "lucide-react";
import { useState } from "react";

type ReportType = "adcopy" | "image" | "landing";

interface ReportInfo {
  title: string;
  description: string;
  icon: React.ReactNode;
  component: React.ReactNode;
  previewImage: string;
  color: string;
  gradient: string;
}

export function ReportShowcase() {
  const [selectedReport, setSelectedReport] = useState<ReportType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<ReportType | null>(null);

  const reports: Record<ReportType, ReportInfo> = {
    adcopy: {
      title: "Ad Copy Analysis",
      description:
        "Get detailed feedback on your ad copy's clarity, persuasiveness, and brand alignment with specific recommendations.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-6 h-6"
        >
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
        </svg>
      ),
      component: <AdCopyEvalDemo />,
      previewImage: "/placeholder.svg?height=300&width=400",
      color: "from-purple-500 to-indigo-600",
      gradient: "bg-gradient-to-br from-purple-50 to-indigo-50",
    },
    image: {
      title: "Image Evaluation",
      description:
        "Analyze your visual content for appeal, brand consistency, and audience resonance with detailed feedback.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-6 h-6"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <circle cx="8.5" cy="8.5" r="1.5"></circle>
          <polyline points="21 15 16 10 5 21"></polyline>
        </svg>
      ),
      component: <ImageEvalDemo />,
      previewImage: "/placeholder.svg?height=300&width=400",
      color: "from-cyan-500 to-blue-600",
      // color: "from-cyan-500 to-blue-600",
      gradient: "bg-gradient-to-br from-cyan-50 to-blue-50",
    },
    landing: {
      title: "Landing Page Review",
      description:
        "Evaluate your landing pages for user experience, visual design, and conversion potential with actionable insights.",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-6 h-6"
        >
          <path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z"></path>
          <path d="m3 9 2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.8 1.1L21 9"></path>
          <path d="M12 3v6"></path>
        </svg>
      ),
      component: <LandingEvalDemo />,
      previewImage: "/placeholder.svg?height=300&width=400",
      color: "from-fuchsia-500 to-pink-600",
      gradient: "bg-gradient-to-br from-fuchsia-50 to-pink-50",
    },
  };

  const openModal = (type: ReportType) => {
    setSelectedReport(type);
    setIsModalOpen(true);
    // Prevent body scrolling when modal is open
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedReport(null);
    // Re-enable body scrolling
    document.body.style.overflow = "auto";
  };

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <section className="py-24 relative" id="demo">
      <div className="absolute inset-0 -z-10">
        <div className="absolute left-0 bottom-0 h-[500px] w-[500px] bg-[radial-gradient(circle_at_center,rgba(124,58,237,0.08),transparent_50%)]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <div className="inline-flex items-center px-3 py-1 mb-4 text-sm font-medium text-violet-800 bg-violet-100 rounded-full">
            Sample Reports
          </div>
          <h2 className="text-3xl sm:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
            See Our Creative Evaluation in Action
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Explore sample reports from our AI-powered creative evaluation
            system that provides detailed insights on various marketing assets.
          </p>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10"
        >
          {(Object.keys(reports) as ReportType[]).map((type) => (
            <motion.div
              key={type}
              variants={item}
              onMouseEnter={() => setHoveredCard(type)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <Card
                className={`h-full border-0 overflow-hidden group cursor-pointer transition-all duration-500 ${reports[type].gradient} hover:shadow-2xl rounded-2xl`}
                onClick={() => openModal(type)}
              >
                <div className="relative h-56 overflow-hidden">
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${reports[type].color} opacity-90 z-10`}
                  ></div>
                  <div className="absolute inset-0 bg-[url('/placeholder.svg?height=500&width=500')] bg-center opacity-10 mix-blend-overlay z-10"></div>

                  <div className="absolute top-0 left-0 w-full h-full z-20 p-6 flex flex-col justify-between">
                    <div className="flex justify-between items-start">
                      <div className="bg-white/20 backdrop-blur-md p-3 rounded-xl text-white">
                        {reports[type].icon}
                      </div>

                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{
                          opacity: hoveredCard === type ? 1 : 0,
                          scale: hoveredCard === type ? 1 : 0.8,
                        }}
                        transition={{ duration: 0.2 }}
                        className="bg-white/20 backdrop-blur-md p-2 rounded-lg text-white"
                      >
                        <Maximize2 size={16} />
                      </motion.div>
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">
                        {reports[type].title}
                      </h3>
                      <div className="w-12 h-1 bg-white/40 rounded-full"></div>
                    </div>
                  </div>

                  <div className="w-full h-full group-hover:scale-105 transition-transform duration-700 ease-out">
                    {type === "adcopy" && (
                      <div className="absolute bottom-0 right-0 w-3/4 h-3/4 p-4 flex items-end justify-end">
                        <div className="bg-white/90 backdrop-blur-md p-4 rounded-xl shadow-lg transform rotate-3 group-hover:rotate-0 transition-transform duration-500">
                          <div className="text-lg font-semibold text-[#1f0aae] mb-1">
                            Meet Your Perfect Sip.
                          </div>
                          <div className="text-sm text-slate-600 mb-2">
                            The Spring 2025 Collection
                          </div>
                          <div className="text-xs text-slate-700 line-clamp-2">
                            From sunrise coffee to evening refreshment, our new
                            collection keeps your drinks just the way you like
                            them.
                          </div>
                        </div>
                      </div>
                    )}
                    {type === "image" && (
                      <div className="absolute bottom-0 right-0 w-3/4 h-3/4 p-4 flex items-end justify-end">
                        <div className="bg-white/90 backdrop-blur-md p-2 rounded-xl shadow-lg transform rotate-3 group-hover:rotate-0 transition-transform duration-500">
                          <img
                            src="https://imagedelivery.net/eqKNURtNCms0uspDZDkHSQ/198a48b4-05d7-41d8-d5ef-ac2c7ec35e00/public"
                            alt="Starbucks products"
                            className="h-32 object-contain rounded-lg"
                          />
                        </div>
                      </div>
                    )}
                    {type === "landing" && (
                      <div className="absolute bottom-0 right-0 w-3/4 h-3/4 p-4 flex items-end justify-end">
                        <div className="bg-white/90 backdrop-blur-md p-2 rounded-xl shadow-lg transform rotate-3 group-hover:rotate-0 transition-transform duration-500">
                          <img
                            src="https://imagedelivery.net/eqKNURtNCms0uspDZDkHSQ/f3457fd0-c977-466b-a98e-57d7034b5a00/public"
                            alt="Starbucks landing page"
                            className="h-32 object-contain rounded-lg"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {reports[type].description}
                  </p>
                  <Button
                    variant="outline"
                    className="rounded-full border-slate-300 hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700 transition-all duration-300 group"
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal(type);
                    }}
                  >
                    View Full Report
                    <ExternalLink className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
        <div className="text-sm text-slate-600 mt-4 text-center">
          We are constantly evolving. The final assessment format may be
          different from the sample Analysis
        </div>
      </div>

      {/* Full-page Modal */}
      <AnimatePresence>
        {isModalOpen && selectedReport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white w-full max-w-6xl max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl relative"
            >
              <div className="sticky top-0 z-10 flex justify-between items-center p-4 bg-white/80 backdrop-blur-md border-b">
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-lg bg-gradient-to-br ${reports[selectedReport].color} text-white`}
                  >
                    {reports[selectedReport].icon}
                  </div>
                  <h3 className="font-semibold text-lg">
                    {reports[selectedReport].title}
                  </h3>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full hover:bg-slate-100"
                  onClick={closeModal}
                >
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </Button>
              </div>
              <div className="overflow-y-auto max-h-[calc(90vh-4rem)]">
                {reports[selectedReport].component}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
